import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Scale, Users, MessageCircle, Heart, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { ServiceType, ROUTE_PATHS } from "@/lib/index";

const iconMap = {
  Scale,
  Users,
  MessageCircle,
  Heart,
};

interface ServiceCardProps {
  service: ServiceType;
  index: number;
}

export function ServiceCard({ service, index }: ServiceCardProps) {
  const IconComponent = iconMap[service.icone as keyof typeof iconMap];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 35,
        delay: index * 0.1,
      }}
    >
      <Card className="h-full border-border bg-card hover:shadow-lg transition-all duration-200 hover:scale-[1.02] group">
        <CardHeader>
          <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-200">
            {IconComponent && <IconComponent className="h-7 w-7" />}
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight">
            {service.titre}
          </CardTitle>
          <CardDescription className="text-base text-muted-foreground mt-2">
            {service.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {service.details.pourQui.slice(0, 3).map((point, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-foreground">
                <span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter>
          <Button
            asChild
            variant="ghost"
            className="w-full group/btn hover:bg-primary/5 transition-colors duration-200"
          >
            <Link to={`${ROUTE_PATHS.SERVICES}#${service.id}`}>
              <span className="flex items-center justify-center gap-2">
                En savoir plus
                <ArrowRight className="h-4 w-4 group-hover/btn:translate-x-1 transition-transform duration-200" />
              </span>
            </Link>
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
}
